import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  FileText,
  ChevronRight,
  Home,
  EyeOff,
} from 'lucide-react';
import { ComplianceBadge } from '../components/ComplianceBadge';
import { ComplianceDisclaimer } from '../components/ComplianceDisclaimer';
import { useAuth } from '../contexts/AuthContext';
import { UpgradeHintCard } from '../components/UpgradeHintCard';
import { dismissUpgradePrompt, getUpgradePromptsForPlacement } from '../lib/upgradeHints';
import { toast } from 'sonner';
import { formatDateForDisplay, formatPropertyAddress } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getTenancyDisplayLabel = (tenancy) => {
  if (Array.isArray(tenancy?.tenants) && tenancy.tenants.length) {
    return tenancy.tenants
      .map((tenant) => tenant?.name || tenant?.email)
      .filter(Boolean)
      .join(', ');
  }
  return 'Tenant';
};

const RRARow = ({ rra, startDateKnown }) => {
  if (!startDateKnown) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
        <span>Add tenancy start date to check RRA document status</span>
      </div>
    );
  }
  if (!rra.required) {
    return (
      <div className="flex items-center gap-2 text-xs text-green-700">
        <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
        <span>RRA Info Sheet — not applicable to this tenancy</span>
      </div>
    );
  }
  if (rra.served) {
    return (
      <div className="flex items-center gap-2 text-xs text-green-700">
        <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
        <span>RRA Info Sheet uploaded</span>
      </div>
    );
  }
  if (rra.overdue) {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-xs text-red-700">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>RRA Info Sheet — deadline passed</span>
          <Link to="/send-documents" className="ml-1 underline hover:text-red-900">
            Send via SIYAK →
          </Link>
        </div>
        <p className="text-xs text-gray-400 pl-5">
          Download your delivery record and upload it here for your records.
        </p>
      </div>
    );
  }
  if (rra.days_remaining !== null && rra.days_remaining <= 30) {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-xs text-amber-600">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>RRA Info Sheet — {rra.days_remaining} days to deadline</span>
          <Link to="/send-documents" className="ml-1 underline hover:text-amber-800">
            Send via SIYAK →
          </Link>
        </div>
        <p className="text-xs text-gray-400 pl-5">
          Download your delivery record and upload it here for your records.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-xs text-blue-600">
        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
        <span>RRA Info Sheet — deadline 31 May 2026</span>
        <Link to="/send-documents" className="ml-1 underline hover:text-blue-800">
          Send via SIYAK →
        </Link>
      </div>
      <p className="text-xs text-gray-400 pl-5">
        Download your delivery record and upload it here for your records.
      </p>
    </div>
  );
};

const TenancySubSection = ({ tenancies, onRefresh }) => {
  const handleStartDateChange = async (tenancyId, value) => {
    try {
      await axios.patch(
        `${API_URL}/api/tenancies/${tenancyId}/start-date`,
        { start_date: value },
        { withCredentials: true }
      );
      onRefresh();
    } catch {
      toast.error('Could not save start date.');
    }
  };

  if (!tenancies?.length) {
    return (
      <p className="text-xs text-gray-400 mt-3">
        No tenancies recorded for this property.
      </p>
    );
  }

  return (
    <div
      className="mt-4 p-4 rounded-lg border border-gray-200 bg-gray-50"
      onClick={(e) => e.stopPropagation()}
    >
      <h4 className="text-sm font-semibold text-gray-900 mb-3">Tenancies</h4>
      <div className="space-y-4">
        {tenancies.map((tenancy) => {
          const rra = tenancy.rra_information_sheet || {};
          const missing = tenancy.missing_documents || [];

          return (
            <div key={tenancy.tenancy_id} className="space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-sm">
                <span className="font-medium text-gray-800">{getTenancyDisplayLabel(tenancy)}</span>
                {tenancy.start_date_known ? (
                  <span className="text-gray-500 text-xs sm:text-sm">
                    {formatDateForDisplay(tenancy.start_date) || 'Start date unavailable'}
                  </span>
                ) : (
                  <input
                    type="date"
                    className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 w-full sm:w-auto"
                    onChange={(e) => {
                      if (e.target.value) {
                        handleStartDateChange(tenancy.tenancy_id, e.target.value);
                      }
                    }}
                  />
                )}
              </div>

              {tenancy.deposit_taken ? (
                <>
                  <div className="flex items-center gap-2 text-xs">
                    {missing.includes('DepositProtection') ? (
                      <>
                        <AlertCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                        <span className="text-red-700">
                          Deposit Protection — not yet uploaded
                        </span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                        <span className="text-green-700">Deposit Protection uploaded</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {missing.includes('PrescribedInfo') ? (
                      <>
                        <AlertCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                        <span className="text-red-700">
                          Prescribed Information — not yet uploaded
                        </span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                        <span className="text-green-700">
                          Prescribed Information uploaded
                        </span>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-xs text-gray-400">No deposit recorded.</div>
              )}

              <RRARow rra={rra} startDateKnown={tenancy.start_date_known} />
            </div>
          );
        })}
      </div>
      <ComplianceDisclaimer className="mt-3" />
    </div>
  );
};

const RRAChecklist = ({ items = [], hasGaps = false }) => {
  if (!items.length) {
    return null;
  }

  const groupedItems = {
    tenancy: items.filter((item) => item.scope === 'tenancy'),
    property: items.filter((item) => item.scope !== 'tenancy'),
  };

  const getStatusIcon = (status) => {
    if (status === 'ok') {
      return <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />;
    }
    if (status === 'overdue') {
      return <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />;
    }
    return <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />;
  };

  return (
    <div
      className="mt-4 p-4 rounded-lg border border-gray-200 bg-gray-50"
      onClick={(e) => e.stopPropagation()}
    >
      <h4 className="text-sm font-semibold text-gray-900 mb-3">
        Renters&apos; Rights Act Checklist (Phase 1 - May 2026)
      </h4>

      <p className="mb-3 text-xs text-gray-500">
        Rows marked <strong>Selected tenancy</strong> depend on tenancy-specific context. Rows marked <strong>Property-wide</strong> apply to the whole property.
      </p>

      <div className="space-y-4">
        {groupedItems.tenancy.length > 0 && (
          <div>
            <div className="mb-2 flex items-center gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Selected tenancy</p>
              <Badge variant="outline" className="border-royal-200 text-royal-700">Tenancy</Badge>
            </div>
            <div className="space-y-2">
              {groupedItems.tenancy.map((item) => (
                <div key={item.key} className="flex items-start gap-2 text-sm">
                  {getStatusIcon(item.status)}
                  <a
                    href={item.gov_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-700 hover:text-blue-700 hover:underline"
                  >
                    {item.label}
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {groupedItems.property.length > 0 && (
          <div>
            <div className="mb-2 flex items-center gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Property-wide</p>
              <Badge variant="outline" className="border-slate-200 text-slate-700">Property</Badge>
            </div>
            <div className="space-y-2">
              {groupedItems.property.map((item) => (
                <div key={item.key} className="flex items-start gap-2 text-sm">
                  {getStatusIcon(item.status)}
                  <a
                    href={item.gov_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-700 hover:text-blue-700 hover:underline"
                  >
                    {item.label}
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {hasGaps && (
        <p className="text-xs text-gray-500 mt-3">
          Some items need attention before May 2026.
        </p>
      )}

      <ComplianceDisclaimer className="mt-3" />
    </div>
  );
};

export const ComplianceDashboard = () => {
  useEffect(() => { document.title = 'Compliance Dashboard - SIYAK'; }, []);
  const navigate = useNavigate();
  const { isLandlord, isAdmin, user, refreshUser } = useAuth();

  const [properties, setProperties] = useState([]);
  const [complianceData, setComplianceData] = useState({});
  const [accountMetrics, setAccountMetrics] = useState(user?.metrics || {});
  const [dismissedHintVersion, setDismissedHintVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    compliant: 0,
    warning: 0,
    nonCompliant: 0,
    bypassed: 0,
  });

  const getMissingBreakdownText = (propertyCompliance, fallbackMissingCount = 0) => {
    const failedRules = Array.isArray(propertyCompliance?.failed_rules)
      ? propertyCompliance.failed_rules
      : [];
    const docCount = failedRules.filter((rule) => rule?.requires_document_upload).length;
    const checklistCount = failedRules.filter((rule) => rule?.requires_checkbox_declaration).length;

    if (docCount === 0 && checklistCount === 0) {
      return `${fallbackMissingCount} missing`;
    }

    const totalActions = docCount + checklistCount;
    return `${totalActions} missing (${docCount} docs, ${checklistCount} checklist)`;
  };

  useEffect(() => {
    if (!isLandlord() && !isAdmin()) {
      navigate('/');
      return;
    }
    fetchDashboardData();
    refreshUser?.(true).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAccountMetrics(user?.metrics || {});
  }, [user?.metrics]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Get properties (backend filters by role)
      const propertiesRes = await axios.get(`${API_URL}/api/properties`, { withCredentials: true });
      setProperties(propertiesRes.data);

      // Get compliance summary
      const complianceRes = await axios.get(
        `${API_URL}/api/documents/compliance/dashboard`,
        { withCredentials: true }
      );
      setComplianceData(complianceRes.data);
      setAccountMetrics(complianceRes.data?.metrics || user?.metrics || {});

      // Calculate stats
      const data = complianceRes.data.properties || [];
      const compliant = data.filter(
        (p) => (p.mot_status || '').toLowerCase() === 'pass'
      ).length;
      const warning = data.filter(
        (p) => (p.mot_status || '').toLowerCase() === 'advisory'
      ).length;
      const bypassed = data.filter(
        (p) => (p.mot_status || '').toLowerCase() === 'bypassed'
      ).length;
      const nonCompliant = data.filter(
        (p) => {
          const status = (p.mot_status || '').toLowerCase();
          return status === 'fail' || status === 'due';
        }
      ).length;

      setStats({
        total: data.length,
        compliant,
        warning,
        nonCompliant,
        bypassed,
      });
    } catch (error) {
      let errorMsg = 'Failed to load compliance data';
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (Array.isArray(error.response?.data)) {
        errorMsg = error.response.data[0]?.msg || errorMsg;
      } else if (error.response?.data?.msg) {
        errorMsg = error.response.data.msg;
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isLandlord() && !isAdmin()) {
    return null;
  }

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto py-8 px-4">
          <div className="flex items-center justify-center min-h-96">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </div>
      </Layout>
    );
  }

  const allProperties = complianceData.properties || [];
  const complianceHints = getUpgradePromptsForPlacement(accountMetrics, 'compliance');
  const handleDismissHint = (hintId) => {
    dismissUpgradePrompt(hintId);
    setDismissedHintVersion((value) => value + 1);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-8 px-4 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Property MOT</h1>
          <p className="text-gray-600 mt-2">
            Monitor MOT status across all your properties
          </p>
        </div>

        {complianceHints.map((hint) => (
          <UpgradeHintCard
            key={`${hint.id}-${dismissedHintVersion}`}
            message={hint.message}
            ctaLabel={hint.ctaLabel}
            onDismiss={() => handleDismissHint(hint.id)}
          />
        ))}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">
                    Total Properties
                  </p>
                  <p className="text-3xl font-bold mt-2">{stats.total}</p>
                </div>
                <Home className="w-12 h-12 text-blue-100" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">MOT Pass</p>
                  <p className="text-3xl font-bold mt-2 text-green-600">
                    {stats.compliant}
                  </p>
                </div>
                <CheckCircle2 className="w-12 h-12 text-green-100" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Warning</p>
                  <p className="text-3xl font-bold mt-2 text-yellow-600">
                    {stats.warning}
                  </p>
                </div>
                <AlertTriangle className="w-12 h-12 text-yellow-100" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">
                    MOT Due
                  </p>
                  <p className="text-3xl font-bold mt-2 text-red-600">
                    {stats.nonCompliant}
                  </p>
                </div>
                <AlertCircle className="w-12 h-12 text-red-100" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">MOT Disabled</p>
                  <p className="text-3xl font-bold mt-2 text-slate-600">
                    {stats.bypassed || 0}
                  </p>
                </div>
                <EyeOff className="w-12 h-12 text-slate-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts */}
        {stats.nonCompliant > 0 && (
          <div className="flex gap-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
            <div className="text-sm text-red-800">
              <strong>{stats.nonCompliant} property/properties</strong> are
              missing required documents and need immediate attention.
            </div>
          </div>
        )}

        {stats.warning > 0 && (
          <div className="flex gap-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
            <div className="text-sm text-yellow-800">
              <strong>{stats.warning} property/properties</strong> have expired
              MOT documents.
            </div>
          </div>
        )}

        {(stats.bypassed || 0) > 0 && (
          <div className="flex gap-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <EyeOff className="w-6 h-6 text-slate-600 flex-shrink-0" />
            <div className="text-sm text-slate-800">
              <strong>{stats.bypassed} property/properties</strong> have SIYAK Property MOT disabled and are using document storage only.
            </div>
          </div>
        )}

        {stats.compliant === (stats.total - (stats.bypassed || 0)) && (stats.total - (stats.bypassed || 0)) > 0 && (
          <div className="flex gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div className="text-sm text-green-800">
              <strong>All properties are MOT Pass!</strong> Keep documents
              updated to maintain MOT status.
            </div>
          </div>
        )}

        {/* Properties List */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold">Properties</h2>

          {allProperties.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Home className="w-12 h-12 text-gray-400 mb-4" />
                <p className="text-gray-600">
                  No properties found. Add properties to track MOT status.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {allProperties.map((propertyData) => {
                const property = properties.find(
                  (p) => p.id === propertyData.property_id
                );

                if (!property) return null;

                const missingCount =
                  propertyData.missing_documents?.length ||
                  propertyData.missing_required_documents?.length ||
                  0;
                const checklistMissingCount = Array.isArray(propertyData.failed_rules)
                  ? propertyData.failed_rules.filter((rule) => rule?.requires_checkbox_declaration).length
                  : 0;
                const totalMissingItems = Math.max(missingCount, missingCount + checklistMissingCount);
                const expiredCount =
                  propertyData.expired_documents?.length ||
                  propertyData.expired_compliance_documents?.length ||
                  0;
                const motStatus = (propertyData.mot_status || '').toLowerCase();
                const isMotBypassed = motStatus === 'bypassed' || Boolean(propertyData.mot_bypassed);

                const status = motStatus || (
                  propertyData.regulatory_compliant
                    ? 'compliant'
                    : expiredCount > 0 && missingCount === 0
                      ? 'warning'
                      : 'non-compliant'
                );

                return (
                  <Card
                    key={propertyData.property_id}
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() =>
                      navigate(
                        `/property/${propertyData.property_id}/documents`
                      )
                    }
                  >
                    <CardContent className="pt-6">
                      <div className="flex flex-col gap-4">
                        {/* Header section */}
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold">
                              {property.title}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              {formatPropertyAddress(property.address, property.postcode)}
                            </p>
                          </div>

                          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <ComplianceBadge
                              status={status}
                              missingCount={totalMissingItems}
                              expiredCount={expiredCount}
                              expiringSoonCount={
                                propertyData.expiring_documents?.length || 0
                              }
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full sm:w-auto"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                  `/property/${propertyData.property_id}/documents`
                                );
                              }}
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              View Docs
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </div>
                        </div>

                        {/* Status indicators - more compact */}
                        <div className="flex flex-wrap gap-2">
                          {isMotBypassed && (
                            <div className="flex items-center gap-1.5 text-xs px-2 py-1 bg-slate-50 text-slate-700 rounded border border-slate-200">
                              <EyeOff className="w-3.5 h-3.5 flex-shrink-0" />
                              <span>SIYAK MOT disabled — storage only</span>
                            </div>
                          )}
                          {missingCount > 0 && (
                            <div className="flex items-center gap-1.5 text-xs px-2 py-1 bg-red-50 text-red-700 rounded border border-red-200">
                              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                              <span>
                                {getMissingBreakdownText(propertyData, missingCount)}
                              </span>
                            </div>
                          )}

                          {expiredCount > 0 && (
                            <div className="flex items-center gap-1.5 text-xs px-2 py-1 bg-yellow-50 text-yellow-700 rounded border border-yellow-200">
                              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                              <span>
                                {expiredCount} expired
                              </span>
                            </div>
                          )}

                          {(propertyData.expiring_documents
                            ?.length || 0) > 0 && (
                            <div className="flex items-center gap-1.5 text-xs px-2 py-1 bg-orange-50 text-orange-700 rounded border border-orange-200">
                              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                              <span>
                                {propertyData.expiring_documents.length} expiring soon
                              </span>
                            </div>
                          )}

                          {!isMotBypassed && (propertyData.mot_status || '').toLowerCase() === 'pass' && (
                            <div className="flex items-center gap-1.5 text-xs px-2 py-1 bg-green-50 text-green-700 rounded border border-green-200">
                              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                              <span>MOT Pass</span>
                            </div>
                          )}

                          {propertyData.high_risk_flags?.length > 0 && (
                            <div className="flex items-center gap-1.5 text-xs px-2 py-1 bg-red-50 text-red-700 rounded border border-red-200">
                              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                              <span>{propertyData.high_risk_flags.length} risk{propertyData.high_risk_flags.length !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                        </div>

                        {/* Additional sections - collapsible on mobile */}
                        {!isMotBypassed && (
                          <div className="space-y-3">
                            <RRAChecklist
                              items={propertyData.rra_checklist?.items || []}
                              hasGaps={propertyData.rra_checklist?.has_gaps || false}
                            />

                            <TenancySubSection
                              tenancies={propertyData.tenancies}
                              onRefresh={fetchDashboardData}
                            />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
